import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { initialSearchValues, type SearchFormValues } from '../schema';
import { DON_HANG_TABLE_TABS, TABLE_PAGE_SIZE } from '../constants';
import { useSearchFieldState, useSearchTable } from '../hooks';
import { DATA_TABLE_COLUMNS } from '../cols-definition';
import { useAuth } from '@/contexts/auth-context';
import type { DonHang } from '@/types/schemas';
import { useCRUD, useForms } from '@/hooks';
import { fetchSoDuTonKho } from '../utils';
import { QUERY_KEYS } from '@/constants';

export const useDonHangTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);

  const {
    data,
    detail,
    fetchDetail,
    addItem,
    currentPage,
    deleteItem,
    refreshData,
    isLoading,
    handlePageChange,
    totalItems,
    updateItem,
    sumTotal,
    searchData,
    isFiltered
  } = useCRUD<DonHang>({
    endpoint: QUERY_KEYS.DON_HANG,
    pageSize: TABLE_PAGE_SIZE,
    sumField: 't_tt_nt',
    useFilter: true
  });

  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    showBulkDelete,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms<DonHang>(fetchDetail);

  const { entity } = useAuth();
  const [detailRowsWithStock, setDetailRowsWithStock] = useState<any[]>([]);

  useEffect(() => {
    let isMounted = true;
    const fetchStock = async () => {
      const chiTiet = (detail as BanHangRecord)?.chi_tiet_data || (detail as BanHangRecord)?.chi_tiet || [];
      if (!chiTiet.length || !entity?.slug || !(detail as BanHangRecord)?.ngay_ct) {
        setDetailRowsWithStock(chiTiet);
        return;
      }

      try {
        const items = chiTiet
          .map((row: any) => ({
            ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
            dvt: row.dvt_data?.uuid || row.dvt || row.ma_vt_data?.dvt_nl || row.ma_vt_data?.dvt || '',
            ma_kho: row.ma_kho_data?.uuid || row.ma_kho || ''
          }))
          .filter((item: any) => item.ma_vt);

        if (items.length === 0) {
          setDetailRowsWithStock(chiTiet);
          return;
        }

        const response = await fetchSoDuTonKho(entity.slug, String((detail as BanHangRecord).ngay_ct), items);

        if (isMounted) {
          const newRows = chiTiet.map((row: any) => {
            const vtUuid = row.ma_vt_data?.uuid || row.ma_vt;
            const khoUuid = row.ma_kho_data?.uuid || row.ma_kho;
            const found = response.results.find(r => r.ma_vt === vtUuid && (r.ma_kho === khoUuid || !r.ma_kho));
            if (found) {
              return { ...row, sl_ton: Number(found.so_du) };
            }
            return row;
          });
          setDetailRowsWithStock(newRows);
        }
      } catch (error) {
        console.error('Failed to fetch stock for detail table', error);
        if (isMounted) {
          setDetailRowsWithStock(chiTiet);
        }
      }
    };
    fetchStock();
    return () => {
      isMounted = false;
    };
  }, [detail, entity?.slug]);

  const searchFieldState = useSearchFieldState();
  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    isFiltered,
    handlePageChange
  });

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      await handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [handleInitialSearch]
  );

  const handleRefresh = useCallback(async () => {
    if (hasFilterParams) {
      await handleRefreshClick();
    } else if (isFiltered) {
      await searchData(searchParams);
    } else {
      await refreshData();
    }

    if (selectedObj?.uuid) {
      await fetchDetail(selectedObj.uuid);
    }
  }, [
    fetchDetail,
    handleRefreshClick,
    hasFilterParams,
    isFiltered,
    refreshData,
    searchData,
    searchParams,
    selectedObj?.uuid
  ]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const displayIsLoading = isLoading || isFiltering;
  const totalAmount = hasFilterParams ? (searchState.totalRow?.t_tt_nt ?? sumTotal) : sumTotal;
  const columns = useMemo(
    () => DATA_TABLE_COLUMNS(handleViewClick, totalAmount, displayIsLoading),
    [displayIsLoading, handleViewClick, totalAmount]
  );
  const tables = useMemo(
    () =>
      DON_HANG_TABLE_TABS.map(tab => ({
        name: tab.name,
        rows: 'status' in tab ? displayData.filter(row => String(row?.status ?? '') === tab.status) : displayData,
        columns,
        icon:
          'color' in tab
            ? createElement('div', {
                className: 'mr-2 size-[7px] rounded-full',
                style: { backgroundColor: tab.color }
              })
            : undefined
      })),
    [columns, displayData]
  );

  return {
    crud: {
      detail,
      fetchDetail,
      addItem,
      deleteItem,
      refreshData,
      updateItem,
      detailRowsWithStock
    },
    selection: {
      selectedObj,
      selectedRowIndex,
      clearSelection
    },
    dialogs: {
      showForm,
      showDelete,
      showBulkDelete,
      isCopyMode
    },
    form: {
      formMode,
      handleCloseForm,
      handleCloseDelete,
      handleAddClick,
      handleEditClick,
      handleDeleteClick,
      handleCopyClick,
      handleRowClick,
      handleCloseBulkDelete
    },
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      searchFieldState: searchFieldState.state,
      searchFieldActions: searchFieldState.actions,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading,
      currentPage: hasFilterParams ? searchState.currentPage : currentPage,
      totalItems: hasFilterParams ? searchState.totalCount : totalItems,
      onPageChange,
      onSearchChange: handleSearchChange,
      onRefresh: handleRefresh
    }
  };
};
